import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";

export type CheckInCategory =
  | "general"
  | "wellness"
  | "nutrition"
  | "progress"
  | "injury"
  | "mindset"
  | "custom";

export type QuestionType =
  | "text"
  | "textarea"
  | "scale"
  | "multiple_choice"
  | "checkbox"
  | "number";

export interface CheckInQuestion {
  id: string;
  template_id: string;
  order_index: number;
  question_text: string;
  question_type: QuestionType;
  required: boolean | null;
  scale_min: number | null;
  scale_max: number | null;
  scale_labels: { min?: string; max?: string } | null;
  options: string[] | null;
  help_text: string | null;
  created_at: string | null;
}

export interface CheckInTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  category: CheckInCategory | null;
  is_active: boolean | null;
  is_system_template: boolean | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  questions?: CheckInQuestion[];
  question_count?: number;
}

export interface CreateTemplateInput {
  name: string;
  description?: string;
  category?: CheckInCategory;
  questions: CreateQuestionInput[];
}

export interface CreateQuestionInput {
  order_index: number;
  question_text: string;
  question_type: QuestionType;
  required?: boolean;
  scale_min?: number;
  scale_max?: number;
  scale_labels?: { min?: string; max?: string };
  options?: string[];
  help_text?: string;
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string;
  category?: CheckInCategory;
  is_active?: boolean;
  questions?: CreateQuestionInput[];
}

export function useCheckInTemplates() {
  const { organization } = useOrganization();
  const [templates, setTemplates] = useState<CheckInTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    if (!organization?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("check_in_templates")
        .select(`
          *,
          questions:check_in_questions(count)
        `)
        .eq("organization_id", organization.id)
        .order("category")
        .order("name");

      if (error) throw error;

      // Transform to include question_count
      const transformed = (data || []).map((t: any) => ({
        ...t,
        question_count: t.questions?.[0]?.count || 0,
      }));

      setTemplates(transformed);
    } catch (error) {
      console.error("Error fetching check-in templates:", error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchTemplateWithQuestions = async (templateId: string): Promise<CheckInTemplate | null> => {
    try {
      const { data, error } = await supabase
        .from("check_in_templates")
        .select(`
          *,
          questions:check_in_questions(*)
        `)
        .eq("id", templateId)
        .single();

      if (error) throw error;

      // Sort questions by order_index
      if (data?.questions) {
        data.questions.sort((a: CheckInQuestion, b: CheckInQuestion) => a.order_index - b.order_index);
      }

      return data as CheckInTemplate;
    } catch (error) {
      console.error("Error fetching template with questions:", error);
      return null;
    }
  };

  const createTemplate = async (input: CreateTemplateInput): Promise<CheckInTemplate | null> => {
    if (!organization?.id) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create the template first
      const { data: template, error: templateError } = await supabase
        .from("check_in_templates")
        .insert({
          organization_id: organization.id,
          created_by: user.id,
          name: input.name,
          description: input.description || null,
          category: input.category || "general",
          is_active: true,
          is_system_template: false,
        })
        .select()
        .single();

      if (templateError) throw templateError;

      // Create questions if provided
      if (input.questions && input.questions.length > 0) {
        const questionsToInsert = input.questions.map((q, index) => ({
          template_id: template.id,
          order_index: q.order_index ?? index,
          question_text: q.question_text,
          question_type: q.question_type,
          required: q.required ?? true,
          scale_min: q.scale_min ?? 1,
          scale_max: q.scale_max ?? 10,
          scale_labels: q.scale_labels || null,
          options: q.options || null,
          help_text: q.help_text || null,
        }));

        const { error: questionsError } = await supabase
          .from("check_in_questions")
          .insert(questionsToInsert);

        if (questionsError) throw questionsError;
      }

      await fetchTemplates();
      return template;
    } catch (error) {
      console.error("Error creating template:", error);
      throw error;
    }
  };

  const updateTemplate = async (templateId: string, input: UpdateTemplateInput): Promise<CheckInTemplate | null> => {
    try {
      // Update template metadata
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };
      if (input.name !== undefined) updateData.name = input.name;
      if (input.description !== undefined) updateData.description = input.description;
      if (input.category !== undefined) updateData.category = input.category;
      if (input.is_active !== undefined) updateData.is_active = input.is_active;

      const { data: template, error: templateError } = await supabase
        .from("check_in_templates")
        .update(updateData)
        .eq("id", templateId)
        .select()
        .single();

      if (templateError) throw templateError;

      // If questions are provided, replace all questions
      if (input.questions !== undefined) {
        // Delete existing questions
        const { error: deleteError } = await supabase
          .from("check_in_questions")
          .delete()
          .eq("template_id", templateId);

        if (deleteError) throw deleteError;

        // Insert new questions
        if (input.questions.length > 0) {
          const questionsToInsert = input.questions.map((q, index) => ({
            template_id: templateId,
            order_index: q.order_index ?? index,
            question_text: q.question_text,
            question_type: q.question_type,
            required: q.required ?? true,
            scale_min: q.scale_min ?? 1,
            scale_max: q.scale_max ?? 10,
            scale_labels: q.scale_labels || null,
            options: q.options || null,
            help_text: q.help_text || null,
          }));

          const { error: insertError } = await supabase
            .from("check_in_questions")
            .insert(questionsToInsert);

          if (insertError) throw insertError;
        }
      }

      await fetchTemplates();
      return template;
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  };

  const deleteTemplate = async (templateId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("check_in_templates")
        .delete()
        .eq("id", templateId);

      if (error) throw error;

      await fetchTemplates();
      return true;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  };

  const duplicateTemplate = async (templateId: string, newName?: string): Promise<CheckInTemplate | null> => {
    try {
      const original = await fetchTemplateWithQuestions(templateId);
      if (!original) throw new Error("Template not found");

      const questions = (original.questions || []).map((q) => ({
        order_index: q.order_index,
        question_text: q.question_text,
        question_type: q.question_type as QuestionType,
        required: q.required ?? true,
        scale_min: q.scale_min ?? undefined,
        scale_max: q.scale_max ?? undefined,
        scale_labels: q.scale_labels ?? undefined,
        options: q.options ?? undefined,
        help_text: q.help_text ?? undefined,
      }));

      return createTemplate({
        name: newName || `${original.name} (Copy)`,
        description: original.description ?? undefined,
        category: (original.category as CheckInCategory) ?? "general",
        questions,
      });
    } catch (error) {
      console.error("Error duplicating template:", error);
      throw error;
    }
  };

  const seedDefaultTemplates = async (): Promise<boolean> => {
    if (!organization?.id) return false;

    try {
      const { error } = await supabase.rpc("seed_default_checkin_templates", {
        p_organization_id: organization.id,
      });

      if (error) throw error;

      await fetchTemplates();
      return true;
    } catch (error) {
      console.error("Error seeding default templates:", error);
      throw error;
    }
  };

  return {
    templates,
    loading,
    refetch: fetchTemplates,
    fetchTemplateWithQuestions,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    seedDefaultTemplates,
  };
}

// Category metadata for UI
export const CHECK_IN_CATEGORIES: Record<CheckInCategory, { label: string; icon: string; color: string }> = {
  general: { label: "General", icon: "clipboard-list", color: "text-muted-foreground" },
  wellness: { label: "Wellness", icon: "heart-pulse", color: "text-destructive" },
  nutrition: { label: "Nutrition", icon: "utensils", color: "text-success" },
  progress: { label: "Progress", icon: "trending-up", color: "text-info" },
  injury: { label: "Injury/Recovery", icon: "bandage", color: "text-warning" },
  mindset: { label: "Mindset", icon: "brain", color: "text-chart-4" },
  custom: { label: "Custom", icon: "settings", color: "text-muted-foreground" },
};

export const QUESTION_TYPES: Record<QuestionType, { label: string; description: string; icon: string }> = {
  text: { label: "Short Text", description: "Single line text input", icon: "type" },
  textarea: { label: "Long Text", description: "Multi-line text for detailed responses", icon: "align-left" },
  scale: { label: "Scale", description: "Numeric rating (1-10)", icon: "sliders" },
  multiple_choice: { label: "Multiple Choice", description: "Single selection from options", icon: "circle-dot" },
  checkbox: { label: "Checkbox", description: "Yes/No toggle", icon: "square-check" },
  number: { label: "Number", description: "Numeric input", icon: "hash" },
};

export const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];

export const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];
